import Tooltip from '@mui/material/Tooltip';
import { useMemo, useRef } from 'react';
import styled from 'styled-components';

const TrackRow = styled.div`
    position: relative;
    height: 26px;
    background: #0e0e1a;
    border-bottom: 1px solid #2a2a3a;
    overflow: hidden;
`;

/* Normal cue block — amber/gold (default), orange (problematic), blue (selected) */
const CueBlock = styled.div`
    position: absolute;
    top: 4px;
    height: 18px;
    border-radius: 2px;
    background: ${({ problematic, selected }) =>
        selected ? 'rgba(100,181,246,0.5)' :
        problematic ? 'rgba(237,108,2,0.65)' : 'rgba(255,193,7,0.35)'};
    border: 1px solid ${({ problematic, selected }) =>
        selected ? '#64b5f6' :
        problematic ? '#ed6c02' : '#ffc107'};
    overflow: hidden;
    cursor: ${({ editMode }) => (editMode ? 'move' : 'default')};
    box-sizing: border-box;
    display: flex;
    align-items: center;
    padding: 0 8px;
    user-select: none;
`;

/* Invisible drag handle on left/right edge of each cue in edit mode */
const EdgeHandle = styled.div`
    position: absolute;
    top: 0;
    ${({ side }) => (side === 'left' ? 'left: 0;' : 'right: 0;')}
    width: 7px;
    height: 100%;
    cursor: ew-resize;
    z-index: 5;
    background: transparent;
    &:hover {
        background: rgba(255, 255, 255, 0.22);
    }
`;

const CueText = styled.span`
    font-size: 9px;
    color: ${({ problematic, selected }) =>
        selected ? '#bbdefb' :
        problematic ? '#ffe0b2' : '#fff8e1'};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    user-select: none;
    pointer-events: none;
    flex: 1;
`;

/**
 * DubSyncTrackBar — renders DubSync cue blocks on the timeline.
 *
 * Props:
 *   cues                  — [{ id, startTime, endTime, text }]
 *   problematicCueIndices — number[]  (0-based indices)
 *   zoomLevel             — px/sec
 *   scrollLeft            — horizontal scroll offset (unused for positioning, kept for compat)
 *   leftOffset            — left padding to align with ruler
 *
 * Edit-mode props (optional):
 *   editMode              — bool: enable drag-resize handles + click-to-select
 *   selectedCueId         — string: currently selected cue id (highlighted blue)
 *   onCueSelect(id)       — called when user clicks a cue
 *   onCueChange(newCues)  — called continuously during drag; parent updates its state
 *   onDragStart()         — called once when a drag begins (use to save undo snapshot)
 *   onCueSplit(id, t)     — called on double-click; parent should split cue at time t
 */
export default function DubSyncTrackBar({
    cues,
    problematicCueIndices = [],
    zoomLevel,
    scrollLeft = 0,
    leftOffset = 0,
    editMode = false,
    selectedCueId = null,
    onCueSelect,
    onCueChange,
    onDragStart,
    onCueSplit,
}) {
    const problematicSet = useMemo(
        () => new Set(problematicCueIndices),
        [problematicCueIndices],
    );

    const dragRef = useRef(null);

    /**
     * Called on pointerdown on either an EdgeHandle or CueBlock body.
     * Captures all subsequent pointer events via window listeners so
     * dragging outside the element still works.
     */
    const startDrag = (e, cueId, edge) => {
        if (!editMode) return;
        e.stopPropagation();
        e.preventDefault();

        const cue = (cues || []).find((c) => c.id === cueId);
        if (!cue) return;

        onCueSelect?.(cueId);
        onDragStart?.();   // save undo snapshot before first change

        dragRef.current = {
            cueId,
            edge,           // 'left' | 'right' | 'move'
            startClientX: e.clientX,
            originalStart: cue.startTime,
            originalEnd:   cue.endTime,
            // snapshot all cue positions so ripple deltas are computed from originals
            allCues: (cues || []).map((c) => ({ ...c })),
            zoomLevel,
            onCueChange,
        };

        const onMove = (moveE) => {
            const drag = dragRef.current;
            if (!drag) return;
            const deltaTime = (moveE.clientX - drag.startClientX) / drag.zoomLevel;
            const cueDuration = drag.originalEnd - drag.originalStart;

            // 5 ms tolerance for "directly adjacent" (split-pair) detection.
            // Using explicit adjacency instead of a '>= originalStart' sweep avoids
            // floating-point ordering problems and closure-captured stale snapshots.
            const ADJ = 0.005;
            const isAdjacent = (c) => Math.abs(c.startTime - drag.originalEnd) <= ADJ;

            const newCues = drag.allCues.map((c) => {
                if (drag.edge === 'left') {
                    // Left-edge resize: only this cue changes
                    if (c.id !== drag.cueId) return c;
                    const newStart = Math.max(0, drag.originalStart + deltaTime);
                    return { ...c, startTime: Math.min(newStart, drag.originalEnd - 0.1) };
                }

                if (drag.edge === 'right') {
                    if (c.id === drag.cueId) {
                        const newEnd = Math.max(drag.originalStart + 0.1, drag.originalEnd + deltaTime);
                        return { ...c, endTime: newEnd };
                    }
                    // Linked-edge: if this cue starts exactly where the dragged cue ends
                    // (i.e. they share a split boundary), snap its start to the new end
                    // so the boundary moves as one — resizing one shrinks/grows the other.
                    if (isAdjacent(c)) {
                        const newSharedBoundary = Math.max(drag.originalStart + 0.1, drag.originalEnd + deltaTime);
                        return { ...c, startTime: newSharedBoundary };
                    }
                    return c;
                }

                // 'move' — shift this cue AND the immediately adjacent next cue (if any)
                // so a split pair stays together as the user repositions one half.
                if (c.id === drag.cueId) {
                    const newStart = Math.max(0, drag.originalStart + deltaTime);
                    return { ...c, startTime: newStart, endTime: newStart + cueDuration };
                }
                if (isAdjacent(c)) {
                    // Adjacent cue follows with the same delta, preserving its own duration
                    const newStart = Math.max(0, drag.originalEnd + deltaTime);
                    return { ...c, startTime: newStart, endTime: newStart + (c.endTime - c.startTime) };
                }
                return c;
            });

            drag.onCueChange?.(newCues);
        };

        const onUp = () => {
            dragRef.current = null;
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
        };

        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
    };

    if (!cues || cues.length === 0) {
        return (
            <TrackRow>
                <span style={{ position: 'absolute', left: 6, top: 7, fontSize: 12, color: '#555' }}>
                    Run ✨ Sync Magic — or right-click for Manual Align
                </span>
            </TrackRow>
        );
    }

    return (
        <TrackRow>
            {cues.map((cue, idx) => {
                // Absolute pixel position within TracksContent — CSS scroll handles viewport offset.
                // scrollLeft is used only for the culling check (not subtracted from position).
                const left  = cue.startTime * zoomLevel + leftOffset;
                const width = (cue.endTime - cue.startTime) * zoomLevel;
                if (left + width < scrollLeft || left > scrollLeft + window.innerWidth) return null;

                const isProblematic = problematicSet.has(idx);
                const isSelected    = cue.id === selectedCueId;

                const block = (
                    <CueBlock
                        key={cue.id}
                        problematic={isProblematic ? 1 : 0}
                        selected={isSelected ? 1 : 0}
                        editMode={editMode ? 1 : 0}
                        style={{ left: `${left}px`, width: `${Math.max(2, width)}px` }}
                        onPointerDown={editMode ? (e) => startDrag(e, cue.id, 'move') : undefined}
                        onClick={editMode ? (e) => { e.stopPropagation(); onCueSelect?.(cue.id); } : undefined}
                        onDoubleClick={editMode && onCueSplit ? (e) => {
                            e.stopPropagation();
                            // Calculate the time at the click position within this cue
                            const offsetX = e.nativeEvent.offsetX ?? 0;
                            const splitAtTime = cue.startTime + offsetX / zoomLevel;
                            const clamped = Math.max(cue.startTime + 0.1, Math.min(cue.endTime - 0.1, splitAtTime));
                            onCueSplit(cue.id, clamped);
                        } : undefined}
                        title={editMode ? 'Drag to shift • Drag edge to resize • Double-click to split' : undefined}
                    >
                        {/* Left resize handle */}
                        {editMode && width > 16 && (
                            <EdgeHandle
                                side="left"
                                onPointerDown={(e) => { e.stopPropagation(); startDrag(e, cue.id, 'left'); }}
                            />
                        )}

                        {width > 24 && (
                            <CueText problematic={isProblematic ? 1 : 0} selected={isSelected ? 1 : 0}>
                                {cue.text}
                            </CueText>
                        )}

                        {/* Right resize handle */}
                        {editMode && width > 16 && (
                            <EdgeHandle
                                side="right"
                                onPointerDown={(e) => { e.stopPropagation(); startDrag(e, cue.id, 'right'); }}
                            />
                        )}
                    </CueBlock>
                );

                if (editMode) return block;

                return (
                    <Tooltip
                        key={cue.id}
                        title={isProblematic ? `⚠ Timing mismatch (>±20%): ${cue.text}` : cue.text}
                        placement="top"
                        arrow
                    >
                        {block}
                    </Tooltip>
                );
            })}
        </TrackRow>
    );
}

